import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { DashboardLayout } from '../components/DashboardLayout';
import { useAuth } from '../context/AuthContext';
import { api } from '../services/api';
import { Check, Crown, Sparkles, Shield, Loader2, CreditCard, CheckCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';

export default function Pricing() {
  const { user, refreshUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [plans, setPlans] = useState([]);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upgrading, setUpgrading] = useState(false);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [confirmDialog, setConfirmDialog] = useState({ open: false, plan: null });
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [pollingPayment, setPollingPayment] = useState(false);

  // Check for payment return from Stripe
  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    const status = searchParams.get('status');
    
    if (sessionId && status === 'success') {
      pollPaymentStatus(sessionId);
    } else if (status === 'cancelled') {
      toast.error('Payment was cancelled');
      // Clear URL params
      setSearchParams({});
    }
  }, [searchParams]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [plansRes, usageRes] = await Promise.all([
        api.getSubscriptionPlans(),
        api.getSubscriptionUsage()
      ]);
      setPlans(plansRes.data);
      setUsage(usageRes.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const pollPaymentStatus = useCallback(async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000;

    if (attempts >= maxAttempts) {
      setPollingPayment(false);
      toast.error('Payment status check timed out. Please refresh the page.');
      setSearchParams({});
      return;
    }

    setPollingPayment(true);
    setPaymentStatus('checking');

    try {
      const response = await api.getCheckoutStatus(sessionId);
      const data = response.data;

      if (data.payment_status === 'paid') {
        setPaymentStatus('success');
        setPollingPayment(false);
        toast.success(`Successfully upgraded to ${data.tier.charAt(0).toUpperCase() + data.tier.slice(1)}!`);
        
        // Refresh user data and plans
        if (refreshUser) await refreshUser();
        await fetchData();
        
        // Clear URL params after short delay
        setTimeout(() => {
          setSearchParams({});
          setPaymentStatus(null);
        }, 3000);
        return;
      } else if (data.status === 'expired') {
        setPaymentStatus('expired');
        setPollingPayment(false);
        toast.error('Payment session expired. Please try again.');
        setSearchParams({});
        return;
      }

      // Continue polling
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    } catch (error) {
      console.error('Error checking payment status:', error);
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), pollInterval);
    }
  }, [refreshUser, setSearchParams]);

  const handleUpgrade = async (plan) => {
    if (plan.is_current || plan.price === 0) return;
    setConfirmDialog({ open: true, plan });
  };

  const confirmUpgrade = async () => {
    const plan = confirmDialog.plan;
    if (!plan) return;

    setUpgrading(true);
    try {
      // Create Stripe checkout session
      const response = await api.createCheckoutSession(plan.id, billingCycle);
      const { checkout_url } = response.data;
      
      // Redirect to Stripe Checkout
      window.location.href = checkout_url;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start checkout');
      setUpgrading(false);
    }
  };

  const getPlanIcon = (planId) => {
    switch (planId) {
      case 'elite': return Crown;
      case 'professional': return Sparkles;
      default: return Shield;
    }
  };

  const getPlanStyle = (planId, isCurrent) => {
    if (isCurrent) return 'border-gold bg-gold/5';
    switch (planId) {
      case 'elite': return 'border-purple-500/50 hover:border-purple-500';
      case 'professional': return 'border-blue-500/50 hover:border-blue-500';
      default: return 'border-[#262626] hover:border-gold/30';
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="min-h-[80vh] flex items-center justify-center">
          <div className="w-8 h-8 border-2 border-gold border-t-transparent rounded-full animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 lg:p-8 max-w-6xl mx-auto" data-testid="pricing-page">
        {/* Payment Processing Overlay */}
        {pollingPayment && (
          <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center">
            <div className="bg-[#0F0F0F] border border-gold/30 p-8 rounded-lg text-center max-w-md mx-4">
              {paymentStatus === 'success' ? (
                <>
                  <CheckCircle size={48} className="text-green-500 mx-auto mb-4" />
                  <h3 className="font-serif text-2xl text-white mb-2">Payment Successful!</h3>
                  <p className="text-muted-foreground">Your membership has been upgraded.</p>
                </>
              ) : (
                <>
                  <Loader2 size={48} className="text-gold mx-auto mb-4 animate-spin" />
                  <h3 className="font-serif text-2xl text-white mb-2">Processing Payment</h3>
                  <p className="text-muted-foreground">Please wait while we confirm your payment...</p>
                </>
              )}
            </div>
          </div>
        )}

        {/* Header */}
        <div className="text-center mb-12">
          <p className="text-gold uppercase tracking-[0.3em] text-xs mb-4">Membership Plans</p>
          <h1 className="font-serif text-4xl text-white mb-4">Choose Your Membership</h1>
          <p className="text-muted-foreground max-w-2xl mx-auto">
            Unlock the full potential of Assagart with our premium memberships. 
            Manage more assets, access advanced insights, and get priority support.
          </p>
        </div>

        {/* Current Usage */}
        {usage && (
          <div className="luxury-card p-6 mb-8">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Current Plan</p>
                <p className="text-xl text-white font-serif">{usage.plan_name}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wider mb-1">Assets Used</p>
                <p className="text-xl text-white">
                  {usage.asset_count} / {usage.asset_limit === 'unlimited' ? '∞' : usage.asset_limit}
                </p>
              </div>
              {usage.asset_limit !== 'unlimited' && (
                <div className="flex-1 max-w-xs">
                  <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Usage</p>
                  <div className="h-2 bg-[#1A1A1A] rounded-full overflow-hidden">
                    <div 
                      className={`h-full ${usage.usage_percentage > 80 ? 'bg-red-500' : 'bg-gold'}`}
                      style={{ width: `${Math.min(usage.usage_percentage, 100)}%` }}
                    />
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">
                    {usage.assets_remaining === 'unlimited' ? 'Unlimited' : `${usage.assets_remaining} remaining`}
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Billing Toggle */}
        <div className="flex justify-center mb-8">
          <div className="flex items-center gap-2 p-1 bg-[#0A0A0A] border border-[#262626]">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-4 py-2 text-sm transition-colors ${
                billingCycle === 'monthly' 
                  ? 'bg-gold text-black' 
                  : 'text-muted-foreground hover:text-white'
              }`}
              data-testid="billing-monthly"
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('yearly')}
              className={`px-4 py-2 text-sm transition-colors ${
                billingCycle === 'yearly' 
                  ? 'bg-gold text-black' 
                  : 'text-muted-foreground hover:text-white'
              }`}
              data-testid="billing-yearly"
            >
              Yearly <span className="text-xs opacity-75">(Save 20%)</span>
            </button>
          </div>
        </div>

        {/* Plans Grid */}
        <div className="grid md:grid-cols-3 gap-6">
          {plans.map((plan) => {
            const Icon = getPlanIcon(plan.id);
            const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price;
            const monthlyEquivalent = billingCycle === 'yearly' ? Math.round(plan.price_yearly / 12) : plan.price;
            
            return (
              <div 
                key={plan.id}
                className={`luxury-card p-6 transition-all duration-300 ${getPlanStyle(plan.id, plan.is_current)}`}
                data-testid={`plan-${plan.id}`}
              >
                {/* Plan Header */}
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className={`p-2 ${plan.is_current ? 'bg-gold/20' : 'bg-[#1A1A1A]'}`}>
                      <Icon size={24} className={plan.is_current ? 'text-gold' : 'text-muted-foreground'} />
                    </div>
                    <div>
                      <h3 className="font-serif text-xl text-white">{plan.name}</h3>
                      <p className="text-xs text-muted-foreground">
                        {plan.asset_limit < 0 ? 'Unlimited assets' : `Up to ${plan.asset_limit} assets`}
                      </p>
                    </div>
                  </div>
                  {plan.is_current && (
                    <span className="px-2 py-1 text-xs bg-gold/20 text-gold uppercase tracking-wider">
                      Current
                    </span>
                  )}
                </div>

                {/* Pricing */}
                <div className="mb-6">
                  <div className="flex items-end gap-1">
                    <span className="text-4xl font-serif text-white">
                      £{monthlyEquivalent}
                    </span>
                    <span className="text-muted-foreground mb-1">/month</span>
                  </div>
                  {billingCycle === 'yearly' && plan.price > 0 && (
                    <p className="text-sm text-gold mt-1">
                      £{price} billed annually
                    </p>
                  )}
                </div>

                {/* Features */}
                <ul className="space-y-3 mb-6">
                  {plan.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-3">
                      <Check size={16} className="text-gold mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-muted-foreground">{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA Button */}
                {plan.is_current ? (
                  <Button 
                    disabled 
                    className="w-full bg-[#1A1A1A] text-muted-foreground cursor-not-allowed"
                  >
                    Current Plan
                  </Button>
                ) : plan.price === 0 ? (
                  <Button 
                    disabled
                    className="w-full bg-[#1A1A1A] text-muted-foreground cursor-not-allowed"
                  >
                    Free Forever
                  </Button>
                ) : (
                  <Button 
                    onClick={() => handleUpgrade(plan)}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid={`upgrade-${plan.id}`}
                  >
                    <CreditCard size={16} />
                    Upgrade to {plan.name}
                  </Button>
                )}
              </div>
            );
          })}
        </div>

        {/* FAQ or Trust Badges */}
        <div className="mt-12 text-center">
          <p className="text-sm text-muted-foreground">
            All plans include SSL encryption, daily backups, and 99.9% uptime guarantee.
          </p>
          <p className="text-xs text-muted-foreground mt-2">
            Secure payment powered by Stripe. Cancel anytime.
          </p>
        </div>

        {/* Confirm Upgrade Dialog */}
        <Dialog open={confirmDialog.open} onOpenChange={(open) => !upgrading && setConfirmDialog({ ...confirmDialog, open })}>
          <DialogContent className="bg-[#0F0F0F] border-[#262626]">
            <DialogHeader>
              <DialogTitle className="font-serif text-xl text-white">
                Upgrade to {confirmDialog.plan?.name}
              </DialogTitle>
              <DialogDescription className="text-muted-foreground">
                You'll be redirected to our secure payment page.
              </DialogDescription>
            </DialogHeader>

            {confirmDialog.plan && (
              <div className="space-y-4">
                <div className="p-4 bg-[#0A0A0A] border border-[#1A1A1A]">
                  <div className="flex justify-between mb-2">
                    <span className="text-muted-foreground">Plan</span>
                    <span className="text-white">{confirmDialog.plan.name}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-muted-foreground">Asset Limit</span>
                    <span className="text-white">
                      {confirmDialog.plan.asset_limit < 0 ? 'Unlimited' : confirmDialog.plan.asset_limit}
                    </span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-muted-foreground">Billing</span>
                    <span className="text-white capitalize">{billingCycle}</span>
                  </div>
                  <div className="flex justify-between border-t border-[#262626] pt-2 mt-2">
                    <span className="text-muted-foreground">Total</span>
                    <span className="text-gold text-lg">
                      £{billingCycle === 'yearly' ? confirmDialog.plan.price_yearly : confirmDialog.plan.price}
                      <span className="text-sm text-muted-foreground">
                        /{billingCycle === 'yearly' ? 'year' : 'month'}
                      </span>
                    </span>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Button 
                    onClick={confirmUpgrade}
                    disabled={upgrading}
                    className="btn-primary flex-1 flex items-center justify-center gap-2"
                    data-testid="confirm-upgrade-btn"
                  >
                    {upgrading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Redirecting...
                      </>
                    ) : (
                      <>
                        <CreditCard size={16} />
                        Proceed to Payment
                      </>
                    )}
                  </Button>
                  <Button 
                    onClick={() => setConfirmDialog({ open: false, plan: null })}
                    disabled={upgrading}
                    className="btn-secondary"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
